/**
 * API client — Clerk-based auth, no local token management.
 */

import type { Conversation, User } from "./types";

const BASE = "/api";

// ── Clerk Token Bridge ──────────────────────────────────────

type TokenGetter = () => Promise<string | null>;
let _getToken: TokenGetter = async () => null;

/** Auth-ready gate: apiFetch blocks until signalAuthReady() is called.
 *  Times out after 10s to prevent infinite hangs on page refresh. */
let _resolveAuthReady: () => void;
let _rejectAuthReady: (reason: Error) => void;
let _authReady = new Promise<void>((resolve, reject) => {
  _resolveAuthReady = resolve;
  _rejectAuthReady = reject;
});

// Safety timeout — if auth never resolves (e.g. Clerk session race), reset
// the gate so subsequent calls can retry instead of hanging forever.
const _authTimeout = setTimeout(() => {
  _rejectAuthReady(new Error("Auth timeout — session not ready"));
  // Reset the gate so future calls after sign-in can still work
  _authReady = new Promise<void>((resolve, reject) => {
    _resolveAuthReady = resolve;
    _rejectAuthReady = reject;
  });
}, 10_000);

/** Called from ClerkTokenBridge to wire Clerk's getToken into apiFetch. */
export function setTokenGetter(fn: TokenGetter): void {
  _getToken = fn;
}

/** Called once ClerkTokenBridge has confirmed a valid session token exists. */
export function signalAuthReady(): void {
  clearTimeout(_authTimeout);
  _resolveAuthReady();
}

// ── Authenticated Fetch ──────────────────────────────────────

export async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  // Wait until auth is confirmed ready (blocks during page load / redirect return)
  // If auth timed out, try fetching a token anyway — Clerk may have loaded since.
  try {
    await _authReady;
  } catch {
    // Auth gate timed out; proceed and let the token getter try anyway.
  }

  const token = await _getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const doFetch = () => fetch(`${BASE}${path}`, { ...options, headers });

  const resp = await doFetch();

  // Retry once on 502 — Docker Desktop's DNS intermittently fails to resolve
  // the backend service name, returning a cached NXDOMAIN. A short delay
  // lets the DNS cache expire and the retry usually succeeds.
  if (resp.status === 502 && !options.signal?.aborted) {
    await new Promise((r) => setTimeout(r, 1000));
    return doFetch();
  }

  return resp;
}

// ── Data Endpoints ───────────────────────────────────────────

export async function listConversations(): Promise<Conversation[]> {
  const resp = await apiFetch("/conversations");
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.conversations ?? [];
}

export async function createConversation(
  title = "New conversation",
  dealId?: string,
): Promise<Conversation | null> {
  const body: Record<string, string> = { title };
  if (dealId) body.deal_id = dealId;
  const resp = await apiFetch("/conversations", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (resp.ok) return resp.json();
  return null;
}

export async function getConversation(
  id: string
): Promise<{ id: string; thread_id: string; title: string; messages: Array<{ role: string; content: string; tool_calls?: Array<{ name: string; args: Record<string, unknown> }> }> } | null> {
  const resp = await apiFetch(`/conversations/${id}`);
  if (resp.ok) return resp.json();
  return null;
}

export async function deleteConversation(id: string): Promise<boolean> {
  const resp = await apiFetch(`/conversations/${id}`, { method: "DELETE" });
  return resp.ok;
}

export async function getUsage(): Promise<{
  total_tokens: number;
  message_count: number;
  limit: number;
  remaining: number;
} | null> {
  const resp = await apiFetch("/usage");
  if (resp.ok) return resp.json();
  return null;
}

// ── Deal Endpoints ───────────────────────────────────────────

export interface DealData {
  id: string;
  parcel_id?: string;
  address?: string;
  county?: string;
  property_type?: string;
  intended_use?: string;
  status: string;
  title?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  closed_at?: string;
}

export async function listDeals(status?: string): Promise<DealData[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const resp = await apiFetch(`/deals${qs}`);
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.deals ?? [];
}

export async function createDeal(params: {
  parcel_id?: string;
  address?: string;
  county?: string;
  property_type?: string;
  intended_use?: string;
  title?: string;
}): Promise<DealData | null> {
  const resp = await apiFetch("/deals", {
    method: "POST",
    body: JSON.stringify(params),
  });
  if (resp.ok) return resp.json();
  return null;
}

export async function getDeal(id: string): Promise<DealData | null> {
  const resp = await apiFetch(`/deals/${id}`);
  if (resp.ok) return resp.json();
  return null;
}

export async function updateDeal(
  id: string,
  fields: Partial<Pick<DealData, "status" | "title" | "notes" | "intended_use" | "property_type" | "county">>
): Promise<DealData | null> {
  const resp = await apiFetch(`/deals/${id}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });
  if (resp.ok) return resp.json();
  return null;
}

export async function getDealDSR(dealId: string): Promise<Record<string, unknown> | null> {
  const resp = await apiFetch(`/deals/${dealId}/dsr`);
  if (resp.ok) return resp.json();
  return null;
}

export async function getDealConversations(dealId: string): Promise<Conversation[]> {
  const resp = await apiFetch(`/deals/${dealId}/conversations`);
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.conversations ?? [];
}

// ── Billing Endpoints ────────────────────────────────────────

export interface BillingStatus {
  tier: string;
  // Credit-based billing (primary)
  credits_used: number;
  credits_limit: number;
  credits_remaining: number;
  credit_transactions?: number;
  // Trial info
  trial_active?: boolean;
  trial_expires_at?: string;
  trial_days_remaining?: number | null;
  max_tool_tier?: string;
  // Legacy token fields
  usage_today: number;
  message_count: number;
  daily_limit: number;
  remaining: number;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
}

export async function getBillingStatus(): Promise<BillingStatus | null> {
  const resp = await apiFetch("/billing/status");
  if (resp.ok) return resp.json();
  return null;
}

export async function createCheckoutSession(priceId: string): Promise<string | null> {
  const resp = await apiFetch("/billing/checkout", {
    method: "POST",
    body: JSON.stringify({
      price_id: priceId,
      success_url: window.location.origin + "/?billing=success",
      cancel_url: window.location.origin + "/?billing=cancel",
    }),
  });
  if (resp.ok) {
    const data = await resp.json();
    return data.url;
  }
  return null;
}

export async function createPortalSession(): Promise<string | null> {
  const resp = await apiFetch("/billing/portal", {
    method: "POST",
  });
  if (resp.ok) {
    const data = await resp.json();
    return data.url;
  }
  return null;
}

// ── Settings Endpoints ───────────────────────────────────────

export interface ChannelInfo {
  channel: string;
  channel_user_id: string;
  metadata: Record<string, unknown>;
  linked_at: string;
}

export interface UsageDay {
  usage_date: string;
  total_tokens: number;
  total_input_tokens: number;
  total_output_tokens: number;
  message_count: number;
}

export interface UserPreferences {
  default_county?: string;
  default_property_type?: string;
  default_market?: string;
  auto_screening?: string;
}

export interface SettingsProfile {
  user: {
    id: string;
    email: string;
    display_name: string | null;
    tier: string;
    created_at: string;
  };
  channels: ChannelInfo[];
  billing: BillingStatus;
  usage_history: UsageDay[];
  preferences: UserPreferences;
}

export async function getSettingsProfile(): Promise<SettingsProfile | null> {
  const resp = await apiFetch("/settings/profile");
  if (resp.ok) return resp.json();
  return null;
}

export interface TelegramLinkToken {
  deep_link: string;
  expires_in: number;
  bot_username: string;
}

export async function initiateTelegramLink(): Promise<TelegramLinkToken | null> {
  const resp = await apiFetch("/settings/channels/telegram/link-token", { method: "POST" });
  if (resp.ok) return resp.json();
  return null;
}

export async function unlinkChannel(channel: string): Promise<boolean> {
  const resp = await apiFetch(`/settings/channels/${channel}`, {
    method: "DELETE",
  });
  return resp.ok;
}

export async function updatePreferences(
  prefs: Partial<UserPreferences>
): Promise<boolean> {
  const resp = await apiFetch("/settings/preferences", {
    method: "PUT",
    body: JSON.stringify(prefs),
  });
  return resp.ok;
}

// ── Boundary Layers (new REST endpoint) ──────────────────────

export async function getBoundaries(
  layerType: "tif_districts" | "tax_districts" | "cities" | "zip_codes",
  bbox?: { north: number; south: number; east: number; west: number }
): Promise<GeoJSON.FeatureCollection | null> {
  const params = new URLSearchParams();
  if (bbox) {
    params.set("bbox", `${bbox.south},${bbox.west},${bbox.north},${bbox.east}`);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const resp = await apiFetch(`/boundaries/${layerType}${qs}`);
  if (resp.ok) return resp.json();
  return null;
}
